import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save, AlertCircle, CheckCircle } from 'lucide-react'
import useUserStore from '@/stores/userStore'
import { CreateUserDto, UpdateUserDto } from '@/services/userService'
import { 
  useInputValidation, 
  emailRules, 
  phoneRules, 
  nameRules, 
  passwordRules,
  formatPhoneNumber,
  formatName
} from '@/hooks/useInputValidation'

const UserForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { createUser, updateUser, currentUser, fetchUserById, loading: _loading } = useUserStore()
  
  const isEditMode = !!id
  
  // Validation hooks for each field
  const email = useInputValidation('', emailRules, [isEditMode, currentUser])
  const phone = useInputValidation('', phoneRules, [isEditMode, currentUser])
  const firstName = useInputValidation('', nameRules, [isEditMode, currentUser])
  const lastName = useInputValidation('', nameRules, [isEditMode, currentUser])
  const password = useInputValidation('', isEditMode ? {} : passwordRules, [isEditMode, currentUser])
  
  const [role, setRole] = useState<'user' | 'merchant' | 'admin'>('user')
  const [isActive, setIsActive] = useState(true)
  const [isVerified, setIsVerified] = useState(false)
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Load user data if in edit mode
  useEffect(() => {
    if (id) {
      fetchUserById(id)
    }
  }, [id, fetchUserById])

  // Update form data when currentUser changes in edit mode
  useEffect(() => {
    if (isEditMode && currentUser && currentUser.user_id === id) {
      email.setValue(currentUser.email || '')
      phone.setValue(currentUser.phone || '')
      firstName.setValue(currentUser.first_name || '')
      lastName.setValue(currentUser.last_name || '')
      setRole(currentUser.primary_role as 'user' | 'merchant' | 'admin' || 'user')
      setIsActive(currentUser.is_active ?? true)
      setIsVerified(currentUser.is_verified || false)
      setMarketingConsent(currentUser.marketing_consent || false)
    }
  }, [isEditMode, currentUser, id])

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value)
    phone.setValue(formatted)
  }

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatName(e.target.value)
    firstName.setValue(formatted)
  }

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatName(e.target.value)
    lastName.setValue(formatted)
  }

  const validateForm = (): boolean => {
    const isEmailValid = email.validate()
    const isPhoneValid = phone.validate()
    const isFirstNameValid = firstName.validate()
    const isLastNameValid = lastName.validate()
    const isPasswordValid = isEditMode ? true : password.validate()

    const errors: Record<string, string> = {}
    
    if (!isEmailValid && email.error) errors.email = email.error
    if (!isPhoneValid && phone.error) errors.phone = phone.error
    if (!isFirstNameValid && firstName.error) errors.first_name = firstName.error
    if (!isLastNameValid && lastName.error) errors.last_name = lastName.error
    if (!isPasswordValid && password.error) errors.password = password.error

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setIsSubmitting(true)
      
      const userData: CreateUserDto | UpdateUserDto = {
        email: email.value,
        phone: phone.value,
        first_name: firstName.value,
        last_name: lastName.value,
        role,
        is_verified: isVerified,
        marketing_consent: marketingConsent
      }

      if (isEditMode) {
        (userData as UpdateUserDto).is_active = isActive
      }

      if (isEditMode) {
        // In edit mode, don't include password
      } else {
        (userData as CreateUserDto).password = password.value
      }

      if (isEditMode && id) {
        await updateUser(id, userData as UpdateUserDto)
        navigate('/users')
      } else {
        await createUser(userData as CreateUserDto)
        navigate('/users')
      }
    } catch (error: any) {
      console.error('Error saving user:', error)
      
      if (error.response?.data?.error?.includes('already exists') || error.message?.includes('already exists')) {
        setFormErrors({ email: 'Un utilisateur avec cet email existe déjà' })
      } else if (error.response?.data?.error) {
        setFormErrors({ general: error.response.data.error })
      } else {
        setFormErrors({ general: 'Erreur lors de l\'enregistrement de l\'utilisateur' })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const getInputClassName = (hasError: boolean, isValid: boolean) => {
    let className = 'w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 '
    
    if (hasError) {
      className += 'border-red-300 focus:border-red-500 focus:ring-red-500 bg-red-50'
    } else if (isValid) {
      className += 'border-green-300 focus:border-green-500 focus:ring-green-500 bg-green-50'
    } else {
      className += 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
    }
    
    return className
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/users')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Retour à la liste
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
          </h1>
          <p className="text-gray-600">
            {isEditMode
              ? 'Modifiez les informations de l\'utilisateur'
              : 'Ajoutez un nouvel utilisateur à la plateforme'}
          </p>
        </div>
      </div>

      {/* General error message */}
      {formErrors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-red-800">{formErrors.general}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Informations personnelles */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Informations personnelles</h2>

            {/* Email */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Email *
                {email.isValid && (
                  <CheckCircle className="h-4 w-4 text-green-500 inline-block ml-2" />
                )}
              </label>
              <input
                type="email"
                value={email.value}
                onChange={email.handleChange}
                onBlur={email.handleBlur}
                className={getInputClassName(!!email.error, email.isValid && email.touched)}
                placeholder="email@example.com"
                required
              />
              {email.error && (
                <p className="text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {email.error}
                </p>
              )}
              {!email.error && email.touched && email.isValid && (
                <p className="text-sm text-green-600 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Format d'email valide
                </p>
              )}
            </div>

            {/* Téléphone */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Téléphone *
                {phone.isValid && (
                  <CheckCircle className="h-4 w-4 text-green-500 inline-block ml-2" />
                )}
              </label>
              <input
                type="tel"
                value={phone.value}
                onChange={handlePhoneChange}
                onBlur={phone.handleBlur}
                className={getInputClassName(!!phone.error, phone.isValid && phone.touched)}
                placeholder="+1 234 567 8900"
                required
              />
              {phone.error && (
                <p className="text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {phone.error}
                </p>
              )}
              {!phone.error && phone.touched && phone.isValid && (
                <p className="text-sm text-green-600 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Format de téléphone valide
                </p>
              )}
              <p className="text-xs text-gray-500">
                Format : +1 234 567 8900 (international)
              </p>
            </div>

            {/* Prénom */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Prénom *
                {firstName.isValid && (
                  <CheckCircle className="h-4 w-4 text-green-500 inline-block ml-2" />
                )}
              </label>
              <input
                type="text"
                value={firstName.value}
                onChange={handleFirstNameChange}
                onBlur={firstName.handleBlur}
                className={getInputClassName(!!firstName.error, firstName.isValid && firstName.touched)}
                placeholder="Jean"
                required
              />
              {firstName.error && (
                <p className="text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {firstName.error}
                </p>
              )}
              {!firstName.error && firstName.touched && firstName.isValid && (
                <p className="text-sm text-green-600 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Format de nom valide
                </p>
              )}
              <div className="flex justify-between text-xs text-gray-500">
                <span>Lettres, espaces, apostrophes et tirets uniquement</span>
                <span>{firstName.value.length}/50</span>
              </div>
            </div>

            {/* Nom */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Nom *
                {lastName.isValid && (
                  <CheckCircle className="h-4 w-4 text-green-500 inline-block ml-2" />
                )}
              </label>
              <input
                type="text"
                value={lastName.value}
                onChange={handleLastNameChange}
                onBlur={lastName.handleBlur}
                className={getInputClassName(!!lastName.error, lastName.isValid && lastName.touched)}
                placeholder="Dupont"
                required
              />
              {lastName.error && (
                <p className="text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {lastName.error}
                </p>
              )}
              {!lastName.error && lastName.touched && lastName.isValid && (
                <p className="text-sm text-green-600 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Format de nom valide
                </p>
              )}
              <div className="flex justify-between text-xs text-gray-500">
                <span>Lettres, espaces, apostrophes et tirets uniquement</span>
                <span>{lastName.value.length}/50</span>
              </div>
            </div>

            {/* Mot de passe (uniquement pour les nouveaux utilisateurs) */}
            {!isEditMode && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Mot de passe *
                  {password.isValid && (
                    <CheckCircle className="h-4 w-4 text-green-500 inline-block ml-2" />
                  )}
                </label>
                <input
                  type="password"
                  value={password.value}
                  onChange={password.handleChange}
                  onBlur={password.handleBlur}
                  className={getInputClassName(!!password.error, password.isValid && password.touched)}
                  placeholder="••••••••"
                  required
                />
                {password.error && (
                  <p className="text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {password.error}
                  </p>
                )}
                {!password.error && password.touched && password.isValid && (
                  <p className="text-sm text-green-600 flex items-center">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Mot de passe sécurisé
                  </p>
                )}
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Minimum 8 caractères</span>
                    <span>{password.value.length}/100</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className={`flex items-center ${/(?=.*[a-z])/.test(password.value) ? 'text-green-600' : 'text-gray-400'}`}>
                      <div className={`h-2 w-2 rounded-full mr-1 ${/(?=.*[a-z])/.test(password.value) ? 'bg-green-500' : 'bg-gray-300'}`} />
                      Minuscule
                    </div>
                    <div className={`flex items-center ${/(?=.*[A-Z])/.test(password.value) ? 'text-green-600' : 'text-gray-400'}`}>
                      <div className={`h-2 w-2 rounded-full mr-1 ${/(?=.*[A-Z])/.test(password.value) ? 'bg-green-500' : 'bg-gray-300'}`} />
                      Majuscule
                    </div>
                    <div className={`flex items-center ${/(?=.*\d)/.test(password.value) ? 'text-green-600' : 'text-gray-400'}`}>
                      <div className={`h-2 w-2 rounded-full mr-1 ${/(?=.*\d)/.test(password.value) ? 'bg-green-500' : 'bg-gray-300'}`} />
                      Chiffre
                    </div>
                    <div className={`flex items-center ${/(?=.*[@$!%*?&])/.test(password.value) ? 'text-green-600' : 'text-gray-400'}`}>
                      <div className={`h-2 w-2 rounded-full mr-1 ${/(?=.*[@$!%*?&])/.test(password.value) ? 'bg-green-500' : 'bg-gray-300'}`} />
                      Spécial
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Rôle et permissions */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Rôle et permissions</h2>

            {/* Rôle */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Rôle
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as 'user' | 'merchant' | 'admin')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="user">Utilisateur</option>
                <option value="merchant">Commerçant</option>
                <option value="admin">Administrateur</option>
              </select>
            </div>

            {/* Statut du compte (uniquement en mode édition) */}
            {isEditMode && (
              <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <div>
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                    Compte actif
                  </label>
                  <p className="text-xs text-gray-500">
                    L'utilisateur peut se connecter et utiliser la plateforme
                  </p>
                </div>
              </div>
            )}

            {/* Statut vérifié */}
            <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                id="is_verified"
                checked={isVerified}
                onChange={(e) => setIsVerified(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <div>
                <label htmlFor="is_verified" className="text-sm font-medium text-gray-700">
                  Email vérifié
                </label>
                <p className="text-xs text-gray-500">
                  L'utilisateur peut se connecter sans vérification d'email
                </p>
              </div>
            </div>

            {/* Consentement marketing */}
            <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                id="marketing_consent"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <div>
                <label htmlFor="marketing_consent" className="text-sm font-medium text-gray-700">
                  Consentement marketing
                </label>
                <p className="text-xs text-gray-500">
                  L'utilisateur accepte de recevoir des communications marketing
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form actions */}
        <div className="pt-6 border-t border-gray-200 flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/users')}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent inline-block mr-2" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 inline-block mr-2" />
                {isEditMode ? 'Mettre à jour' : 'Créer l\'utilisateur'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default UserForm;
